import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Colors from '../common/NekoTheme';
import { Icon, InlineIcon, IconifyIcon } from '@iconify/react';
import folderIcon from '@iconify/icons-mdi/folder';
import folderOpen from '@iconify/icons-mdi/folder-open';
import imageMultipleOutline from '@iconify/icons-mdi/image-multiple-outline';
import chevronRight from '@iconify/icons-mdi/chevron-right';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import tagIcon from '@iconify/icons-mdi/tag';

const StyledNekoFinder = props => {

  const { chevron = true } = props;

  const children = React.Children.map(props.children, child => {
    return React.cloneElement(child, {
      chevron
    });
  });

  return (
    children
  );
};

const FinderTitleContainer = Styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-family: Lato;
`;

const FinderTitle = Styled.div`
  align-items: center;
  display: flex;

  &.can-expand {
    cursor: pointer;
  }

  &.selected {
    color: #956DBE;
  }

  p {
    margin: 0 0 0 8px;
  }
`;

const ChevronSpace = Styled.div`
  height: 24px;
  width: 24px;
`;

const ItemContainer = Styled.div`
  border-left: 1px solid ${Colors.finderBorder};
  margin-left: 12px;
  padding-left: 6px;

  &.no-line {
    border-left: 0;
    padding-left: 12px;
  }

  &.no-chevron {
    border-left: 0;
    margin-left: 0;
    padding-left: 0;
  }
`;

const StyledNekoFinderItem = props => {

  const defaultIcons = {
    gallery: imageMultipleOutline,
    tag: tagIcon
  }

  const { title, chevron, rightElement = null, rightElementStyle = { marginLeft: '5px' }, showRightElement = false, selected = false,
    draggable = false, onDragStart, onDragOver, onDragEnd, onDrop, isExpanded = false, dragging = false, rightAction = null,
    rightActionOnHover = null, onDragLeave, preventClose = false } = props;
  const isCustomizedIcon = !!props.icon;
  const hasChild = !!props.children;
  const [ expanded, setExpanded ] = React.useState(isExpanded);
  const [ icon, setIcon ] = React.useState(() => {
    if (typeof props.icon === 'string' && Object.keys(defaultIcons).includes(props.icon)) {
      return defaultIcons[props.icon];
    } else if (props.icon) {
      return props.icon;
    }
    return folderIcon
  });
  const isFolder = Boolean(icon);
  const [ showRightAction, setShowRightAction ] = React.useState(false);

  const onClick = () => {
    if (props.onClick) {
      props.onClick();
    }
    if (!isFolder && !hasChild) return;
    if (preventClose && expanded) return;
    if (!isCustomizedIcon) {
      setIcon(expanded ? folderIcon : folderOpen);
    }
    setExpanded(!expanded);
  }

  const children = React.Children.map(props.children, child => {
    return React.cloneElement(child, {
      chevron
    });
  });

  const iconColor = dragging || selected ? Colors.purple : (selected ? Colors.black : Colors.blue);

  return (
    <div>
      <FinderTitleContainer onMouseEnter={() => setShowRightAction(true)} onMouseLeave={() => setShowRightAction(false)}>
        <FinderTitle onClick={onClick} onMouseEnter={props.onMouseEnter} onMouseLeave={props.onMouseLeave} draggable={draggable} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} className={`${isFolder || children || props.onClick ? 'can-expand' : ''} ${selected ? 'selected' : ''}`} onDragLeave={onDragLeave} onDragEnd={onDragEnd}>
          {chevron
            ? (isFolder && hasChild)
              ? <Icon icon={expanded ? chevronDown : chevronRight} width="24" height="24" />
              : <ChevronSpace></ChevronSpace>
            : null
          }
          <Icon icon={icon} color={iconColor} width="24" height="24" />
          <p>{ title }</p>
          {showRightElement &&
            <div style={rightElementStyle}>{rightElement}</div>
          }
        </FinderTitle>
        {showRightAction ? (rightActionOnHover || rightAction) : rightAction}
      </FinderTitleContainer>
      {expanded && <ItemContainer className={`${isFolder ? '' : 'no-line'} ${chevron ? '' : 'no-chevron'}`}>{children}</ItemContainer>}
    </div>
  )
}

const NekoFinder = (props) => {
  return (<StyledNekoFinder {...props} />);
}

NekoFinder.propTypes = {
  chevron: PropTypes.bool
};

NekoFinder.defaultProps = {
  chevron: true
};

const NekoFinderItem = (props) => {
  return (<StyledNekoFinderItem {...props} />);
}

NekoFinderItem.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.instanceOf(IconifyIcon), PropTypes.oneOf(['gallery'])]),
  title: PropTypes.string,
  isExpanded: PropTypes.bool
};

NekoFinderItem.defaultProps = {
  icon: folderIcon,
  title: undefined,
  isExpanded: false
};

export { NekoFinder, NekoFinderItem };
